import React, { useEffect, useState } from 'react';
import { ReactSortable } from 'react-sortablejs';
import './style.scss';
import { Empty } from 'antd';
import Loading from '@/components/common/loading';

const sortableOptions = {
  animation: 150,
  fallbackOnBody: true,
  swapThreshold: 0.65,
  ghostClass: 'ghost',
  group: 'shared',
  handle: '.sortable-view', // handle's class
  forceFallback: true,
};

let timeout = null;

export default function NestedSortable({
  data = [],
  renderItem = (item, index) => null,
  child = 'children',
  id = 'id',
  isLoading = false,
  isExpandAll = false,
  onChange,
  onMove,
}) {
  const [oldBlock, setOldBlock] = useState(JSON.parse(JSON.stringify(data)));
  const [block, setBlock] = useState(data);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    setBlock(data);
    setTimeout(() => {
      setOldBlock(JSON.parse(JSON.stringify(data)));
    }, 0);
  }, [data]);

  const updateList = (nextBlock) => {
    setBlock(nextBlock);
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      if (selectedItem)
        onChange(
          JSON.parse(JSON.stringify(oldBlock)),
          typeof nextBlock === 'function' ? block : nextBlock,
          selectedItem,
        );
    }, 500);
  };

  const chooseItem = (data) => {
    setSelectedItem(data.item.id);
  };

  const moveItem = (e) => {
    const dragItems = document.getElementsByClassName('sortable-drag');
    onMove(e, dragItems[(dragItems?.length || 1) - 1]);
  };

  return (
    <div className="nested-sortable-container">
      <Loading isLoading={isLoading} />
      {block.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      <div data-level={0}>
        <ReactSortable
          list={data}
          setList={updateList}
          onChoose={chooseItem}
          {...sortableOptions}
          onChange={moveItem}
        >
          {block.map((block, blockIndex) => (
            <BlockWrapper
              key={block.id || blockIndex}
              block={block}
              blockIndex={[blockIndex]}
              index={blockIndex}
              setBlocks={updateList}
              renderItem={renderItem}
              onChoose={chooseItem}
              child={child}
              id={id}
              level={0}
              isExpandAll={isExpandAll}
              onMove={moveItem}
            />
          ))}
        </ReactSortable>
      </div>
    </div>
  );
}
function Container({
  block,
  blockIndex,
  setBlocks,
  renderItem,
  child,
  id,
  level,
  onChoose,
  isExpandAll,
  onMove,
}) {
  return (
    <div data-level={level + 1}>
      <ReactSortable
        onChange={onMove}
        key={block.id}
        list={block?.[child]}
        onChoose={onChoose}
        setList={(currentList) => {
          setBlocks((sourceList) => {
            const tempList = [...sourceList];
            const _blockIndex = [...blockIndex];
            const lastIndex = _blockIndex.pop();
            const lastArr = _blockIndex.reduce((arr, i) => arr[i][child], tempList);
            lastArr[lastIndex][child] = currentList;
            return tempList;
          });
        }}
        {...sortableOptions}
      >
        {block?.[child]?.map((childBlock, index) => {
          return (
            <BlockWrapper
              key={childBlock.id}
              block={childBlock}
              index={index}
              blockIndex={[...blockIndex, index]}
              setBlocks={setBlocks}
              renderItem={renderItem}
              child={child}
              id={id}
              level={level + 1}
              onChoose={onChoose}
              isExpandAll={isExpandAll}
              onMove={onMove}
            />
          );
        })}
      </ReactSortable>
    </div>
  );
}

function BlockWrapper({
  block,
  index,
  blockIndex,
  setBlocks,
  renderItem,
  child,
  level = 0,
  id,
  onChoose,
  isExpandAll,
  onMove,
}) {
  const [isExpand, setIsExpand] = useState(false);

  useEffect(() => {
    setIsExpand(isExpandAll);
  }, [isExpandAll]);

  if (!block) return null;
  return (
    <div id={block?.[id]} className="block">
      {renderItem(block, index, isExpand, setIsExpand, level)}
      {block?.[child]?.length && isExpand && (
        <Container
          id={id}
          block={block}
          setBlocks={setBlocks}
          blockIndex={blockIndex}
          renderItem={renderItem}
          child={child}
          level={level}
          onChoose={onChoose}
          isExpandAll={isExpandAll}
          onMove={onMove}
        />
      )}
    </div>
  );
}
